/**
 * Rate Limiting Service
 * Implements client-side and API rate limiting for security
 */

import { auditService } from '../auth/audit-service';

export interface RateLimitRule {
  id: string;
  name: string;
  windowMs: number; // Time window in milliseconds
  maxRequests: number; // Maximum requests per window
  keyGenerator?: (request: RateLimitRequest) => string; // Custom key generation
  skipRules?: Array<(request: RateLimitRequest) => boolean>; // Skip conditions
  onLimitReached?: (key: string, request: RateLimitRequest) => void; // Callback when limit reached
  blockDurationMs?: number; // How long to block after limit reached
}

export interface RateLimitRequest {
  endpoint: string;
  method: string;
  userId?: string;
  ip?: string;
  userAgent?: string;
  timestamp: number;
  headers?: Record<string, string>;
}

export interface RateLimitEntry {
  count: number;
  firstRequestTime: number;
  lastRequestTime: number;
  blockedUntil?: number;
  violations: number;
}

export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  resetTime: number;
  retryAfter?: number; // Seconds to wait if blocked
  rule: RateLimitRule;
}

// Default rate limiting rules
export const defaultRateLimitRules: RateLimitRule[] = [
  // General API limits
  {
    id: 'api-general',
    name: 'General API Rate Limit',
    windowMs: 60 * 1000, // 1 minute
    maxRequests: 100,
    keyGenerator: (req) => `general:${req.userId || req.ip || 'anonymous'}`
  },

  // Authentication limits
  {
    id: 'auth-login',
    name: 'Login Rate Limit',
    windowMs: 15 * 60 * 1000, // 15 minutes
    maxRequests: 5,
    keyGenerator: (req) => `auth:${req.ip || 'unknown'}:${req.endpoint}`,
    blockDurationMs: 30 * 60 * 1000, // 30 minutes block
    onLimitReached: (key, req) => {
      auditService.logAction({
        action: 'security:rate_limit_exceeded',
        resource_type: 'rate_limit',
        resource_id: key,
        details: {
          endpoint: req.endpoint,
          ip: req.ip,
          userAgent: req.userAgent,
          rule: 'auth-login'
        },
        success: false
      });
    }
  },

  // Password reset limits
  {
    id: 'auth-password-reset',
    name: 'Password Reset Rate Limit',
    windowMs: 60 * 60 * 1000, // 1 hour
    maxRequests: 3,
    keyGenerator: (req) => `password-reset:${req.ip || 'unknown'}`,
    blockDurationMs: 60 * 60 * 1000 // 1 hour block
  },

  // API key generation limits
  {
    id: 'api-key-generation',
    name: 'API Key Generation Rate Limit',
    windowMs: 24 * 60 * 60 * 1000, // 24 hours
    maxRequests: 10,
    keyGenerator: (req) => `api-key:${req.userId || req.ip || 'anonymous'}`
  },

  // File upload limits
  {
    id: 'file-upload',
    name: 'File Upload Rate Limit',
    windowMs: 60 * 1000, // 1 minute
    maxRequests: 20,
    keyGenerator: (req) => `upload:${req.userId || req.ip || 'anonymous'}`
  },

  // Search/Query limits
  {
    id: 'search-queries',
    name: 'Search Query Rate Limit',
    windowMs: 60 * 1000, // 1 minute
    maxRequests: 50,
    keyGenerator: (req) => `search:${req.userId || req.ip || 'anonymous'}`
  },

  // LakeFS operations
  {
    id: 'lakefs-operations',
    name: 'LakeFS Operations Rate Limit',
    windowMs: 60 * 1000, // 1 minute
    maxRequests: 30,
    keyGenerator: (req) => `lakefs:${req.userId || req.ip || 'anonymous'}`
  },

  // Export operations
  {
    id: 'export-operations',
    name: 'Export Operations Rate Limit',
    windowMs: 5 * 60 * 1000, // 5 minutes
    maxRequests: 5,
    keyGenerator: (req) => `export:${req.userId || req.ip || 'anonymous'}`
  },

  // Strict limits for suspicious activity
  {
    id: 'suspicious-activity',
    name: 'Suspicious Activity Rate Limit',
    windowMs: 60 * 1000, // 1 minute
    maxRequests: 10,
    keyGenerator: (req) => `suspicious:${req.ip || 'unknown'}`,
    blockDurationMs: 60 * 60 * 1000, // 1 hour block
    skipRules: [
      (req) => !!req.userId && req.headers?.['x-trusted-client'] === 'true'
    ]
  }
];

export class RateLimiterService {
  private static instance: RateLimiterService;
  private rules: Map<string, RateLimitRule>;
  private store: Map<string, RateLimitEntry>;
  private cleanupInterval: number | null = null;

  private constructor(rules: RateLimitRule[] = defaultRateLimitRules) {
    this.rules = new Map(rules.map(rule => [rule.id, rule]));
    this.store = new Map();
    this.startCleanupProcess();
  }

  static getInstance(rules?: RateLimitRule[]): RateLimiterService {
    if (!RateLimiterService.instance) {
      RateLimiterService.instance = new RateLimiterService(rules);
    }
    return RateLimiterService.instance;
  }

  /**
   * Check if a request should be allowed based on rate limiting rules
   */
  checkLimit(request: RateLimitRequest, ruleIds?: string[]): RateLimitResult {
    const now = Date.now();
    const rulesToCheck = ruleIds
      ? ruleIds.map(id => this.rules.get(id)).filter(Boolean) as RateLimitRule[]
      : Array.from(this.rules.values());

    // Find the most restrictive violated rule
    for (const rule of rulesToCheck) {
      // Check skip rules
      if (rule.skipRules?.some(skipFn => skipFn(request))) {
        continue;
      }

      const key = rule.keyGenerator ? rule.keyGenerator(request) : this.defaultKeyGenerator(request);
      const entry = this.store.get(key) || this.createNewEntry(now);

      // Check if currently blocked
      if (entry.blockedUntil && now < entry.blockedUntil) {
        return {
          allowed: false,
          remaining: 0,
          resetTime: entry.blockedUntil,
          retryAfter: Math.ceil((entry.blockedUntil - now) / 1000),
          rule
        };
      }

      // Check if window has expired
      if (now - entry.firstRequestTime > rule.windowMs) {
        this.resetEntry(entry, now);
      }

      // Check if limit exceeded
      if (entry.count >= rule.maxRequests) {
        entry.violations++;

        // Apply block if configured
        if (rule.blockDurationMs) {
          entry.blockedUntil = now + rule.blockDurationMs;
        }

        // Call violation callback
        if (rule.onLimitReached) {
          rule.onLimitReached(key, request);
        }

        this.store.set(key, entry);

        return {
          allowed: false,
          remaining: 0,
          resetTime: entry.firstRequestTime + rule.windowMs,
          retryAfter: rule.blockDurationMs ? Math.ceil(rule.blockDurationMs / 1000) : undefined,
          rule
        };
      }

      // Update entry and allow request
      entry.count++;
      entry.lastRequestTime = now;
      this.store.set(key, entry);
    }

    // If no rule was violated, find the most restrictive rule to return info
    const mostRestrictive = rulesToCheck.reduce((prev, current) => {
      if (!prev) return current;
      return current.maxRequests / current.windowMs < prev.maxRequests / prev.windowMs ? current : prev;
    });

    if (mostRestrictive) {
      const key = mostRestrictive.keyGenerator
        ? mostRestrictive.keyGenerator(request)
        : this.defaultKeyGenerator(request);
      const entry = this.store.get(key) || this.createNewEntry(now);

      return {
        allowed: true,
        remaining: Math.max(0, mostRestrictive.maxRequests - entry.count),
        resetTime: entry.firstRequestTime + mostRestrictive.windowMs,
        rule: mostRestrictive
      };
    }

    // No rules applied - allow request
    return {
      allowed: true,
      remaining: Infinity,
      resetTime: now + 60000, // Default 1 minute window
      rule: {
        id: 'default',
        name: 'No Limit',
        windowMs: 60000,
        maxRequests: Infinity
      }
    };
  }

  /**
   * Check multiple rules and return the most restrictive result
   */
  checkMultipleRules(request: RateLimitRequest, ruleIds: string[]): RateLimitResult[] {
    return ruleIds.map(ruleId => {
      const rule = this.rules.get(ruleId);
      if (!rule) {
        throw new Error(`Rate limit rule not found: ${ruleId}`);
      }
      return this.checkLimit(request, [ruleId]);
    });
  }

  /**
   * Add a new rate limiting rule
   */
  addRule(rule: RateLimitRule): void {
    this.rules.set(rule.id, rule);
  }

  /**
   * Remove a rate limiting rule
   */
  removeRule(ruleId: string): void {
    this.rules.delete(ruleId);
  }

  /**
   * Update an existing rule
   */
  updateRule(ruleId: string, updates: Partial<RateLimitRule>): void {
    const existing = this.rules.get(ruleId);
    if (existing) {
      this.rules.set(ruleId, { ...existing, ...updates });
    }
  }

  /**
   * Get current stats for a key
   */
  getStats(key: string): RateLimitEntry | null {
    return this.store.get(key) || null;
  }

  /**
   * Get all current rate limit entries
   */
  getAllStats(): Map<string, RateLimitEntry> {
    return new Map(this.store);
  }

  /**
   * Clear rate limit data for a specific key
   */
  clearKey(key: string): void {
    this.store.delete(key);
  }

  /**
   * Clear all rate limit data
   */
  clearAll(): void {
    this.store.clear();
  }

  /**
   * Get list of currently blocked keys
   */
  getBlockedKeys(): Array<{ key: string; entry: RateLimitEntry; unblockTime: number }> {
    const now = Date.now();
    const blocked: Array<{ key: string; entry: RateLimitEntry; unblockTime: number }> = [];

    this.store.forEach((entry, key) => {
      if (entry.blockedUntil && entry.blockedUntil > now) {
        blocked.push({ key, entry, unblockTime: entry.blockedUntil });
      }
    });

    return blocked.sort((a, b) => a.unblockTime - b.unblockTime);
  }

  /**
   * Manually block a key for a specific duration
   */
  blockKey(key: string, durationMs: number): void {
    const now = Date.now();
    const entry = this.store.get(key) || this.createNewEntry(now);
    entry.blockedUntil = now + durationMs;
    entry.violations++;
    this.store.set(key, entry);
  }

  /**
   * Manually unblock a key
   */
  unblockKey(key: string): void {
    const entry = this.store.get(key);
    if (entry) {
      delete entry.blockedUntil;
      this.store.set(key, entry);
    }
  }

  /**
   * Create HTTP client interceptor for automatic rate limiting
   */
  createHTTPInterceptor() {
    return {
      request: (config: any) => {
        const request: RateLimitRequest = {
          endpoint: config.url || '',
          method: config.method?.toUpperCase() || 'GET',
          timestamp: Date.now(),
          headers: config.headers
        };

        // Determine appropriate rule based on endpoint
        let ruleIds: string[] = [];

        if (request.endpoint.includes('/auth/')) {
          if (request.endpoint.includes('/login')) {
            ruleIds = ['auth-login'];
          } else if (request.endpoint.includes('/password-reset')) {
            ruleIds = ['auth-password-reset'];
          }
        } else if (request.endpoint.includes('/lakefs/')) {
          ruleIds = ['lakefs-operations'];
        } else if (request.endpoint.includes('/search')) {
          ruleIds = ['search-queries'];
        } else if (request.endpoint.includes('/upload')) {
          ruleIds = ['file-upload'];
        } else {
          ruleIds = ['api-general'];
        }

        const result = this.checkLimit(request, ruleIds);

        if (!result.allowed) {
          const error = new Error(`Rate limit exceeded for ${result.rule.name}`);
          (error as any).rateLimitResult = result;
          (error as any).status = 429;
          throw error;
        }

        // Add rate limit headers to request
        if (!config.headers) {
          config.headers = {};
        }

        config.headers['X-RateLimit-Limit'] = result.rule.maxRequests.toString();
        config.headers['X-RateLimit-Remaining'] = result.remaining.toString();
        config.headers['X-RateLimit-Reset'] = Math.ceil(result.resetTime / 1000).toString();

        return config;
      }
    };
  }

  private defaultKeyGenerator(request: RateLimitRequest): string {
    return `${request.endpoint}:${request.userId || request.ip || 'anonymous'}`;
  }

  private createNewEntry(timestamp: number): RateLimitEntry {
    return {
      count: 0,
      firstRequestTime: timestamp,
      lastRequestTime: timestamp,
      violations: 0
    };
  }

  private resetEntry(entry: RateLimitEntry, timestamp: number): void {
    entry.count = 0;
    entry.firstRequestTime = timestamp;
    entry.lastRequestTime = timestamp;
    delete entry.blockedUntil;
  }

  private startCleanupProcess(): void {
    // Clean up expired entries every 5 minutes
    this.cleanupInterval = window.setInterval(() => {
      this.cleanupExpiredEntries();
    }, 5 * 60 * 1000);
  }

  private cleanupExpiredEntries(): void {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours

    this.store.forEach((entry, key) => {
      // Remove entries that are old and not blocked
      if (!entry.blockedUntil && (now - entry.lastRequestTime) > maxAge) {
        this.store.delete(key);
      }
      // Remove expired blocks
      else if (entry.blockedUntil && now > entry.blockedUntil) {
        delete entry.blockedUntil;
      }
    });
  }

  /**
   * Destroy the service and clean up resources
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.store.clear();
  }
}

// Singleton instance
export const rateLimiter = RateLimiterService.getInstance();

export default rateLimiter;